import { add, endOfDay, isAfter, isBefore, startOfDay } from 'date-fns';
import { getEventSheetsFuse, getSheetEventTitle } from 'pages/ScheduleV6/utils';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { startEndFromTravelTimeOnly } from 'store/schedule/schedulePageOperation';
import { selectVisibleSheetsForDate } from 'store/schedule/scheduleSelectors';


const getSheetHeightInPixels = (sheet, title, selectedDate) => {

  const defaultHeight = 47;
  // title
  const maxLineLength = 21;
  const titleLines = Math.ceil((title?.length || 0) / maxLineLength);
  const titleHeight = (titleLines * 20) + 10;

  const contractorsHeight = 26;
  const timeHeight = 27;

  const prevDayBlock = isBefore(new Date(sheet.hours.start), new Date(selectedDate))
    ? 35
    : 0;

  let contentBlockHeight;
  if (sheet.canceledAt) {
    // cancel icon + "Canceled" label + margins ≈ 68px; note text adds ~15px per line; 24 is a paddings
    const cancelNoteLines = sheet.cancelNote
      ? Math.ceil(sheet.cancelNote.length / 22)
      : 0;
    const cancelNoteHeight = cancelNoteLines * 15;
    contentBlockHeight = Math.max(140, 52 + cancelNoteHeight + 24);
  } else {
    const workersHeight = sheet.workers?.length
      ? 16 + 8 + sheet.workers.length * 28
      : 0;
    const equipmentHeight = sheet.equipment?.length
      ? 16 + 8 + sheet.equipment.length * 28
      : 0;
    const resourcesBlockHeight = (workersHeight > 0 || equipmentHeight > 0) ? 13 : 0;
    contentBlockHeight = workersHeight + equipmentHeight + resourcesBlockHeight;
  }

  const notesLines = sheet.notes?.trim()
    ? Math.ceil((sheet.notes.trim().slice(0, 50) + (sheet.notes.length > 50 ? '...' : '')).length / 22)
    : 0;
  const notesHeight = sheet.notes && sheet.notes.trim()
    ? 32 + (notesLines * 15.5)
    : 0;

  const totalHeight =
    defaultHeight +
    titleHeight +
    contractorsHeight +
    (sheet.hours?.start ? timeHeight : 0) +
    contentBlockHeight +
    notesHeight +
    prevDayBlock;
  console.log('totalHeight: ', totalHeight);

  return totalHeight;
}

/**
 * Maps adapted schedule sheets from the store into FullCalendar v6
 * event objects for time-based views (daily/weekly).
 *
 * For now we:
 * - reuse adapted data from schedulePageOperation (start/end, originalStart/originalEnd);
 * - attach the full adapted sheet object into extendedProps so event
 *   renderers (later ScheduleEventV6) have everything they need;
 * - filter sheets to the current day to avoid rendering irrelevant events.
 */
export const useV6TimeEvents = () => {
  const allSheets = useSelector(selectVisibleSheetsForDate);
  const searchValue = useSelector((store) => store.schedule.searchValue);
  const showAllLocations = useSelector((store) => store.schedule.showAllLocations);
  const selectedDate = useSelector((store) => store.schedule.selectedDate);
  const locations = useSelector((state) => state.personalProfile.activeLocations);
  const timeFormat = useSelector((store) => store.personalProfile.organization?.settings?.timeFormat);

  const visibleSheets = useMemo(() => {
    const fuse = getEventSheetsFuse(allSheets);

    if (searchValue) return fuse.search(searchValue);
    return allSheets;
  }, [allSheets, searchValue]);

  const events = useMemo(() => {
    if (!Array.isArray(visibleSheets) || !visibleSheets.length) return [];

    return visibleSheets.map((entry) => {
      const { sheet, start, end } = entry;
      const title = getSheetEventTitle(entry, 'short');
      const locationColor = showAllLocations
        ? locations.find(({ _id }) => sheet.tenantLocationId === _id)?.color
        : null;

      const eventStart = start
        ? new Date(start)
        : (sheet.startTimeDate
          ? new Date(sheet.startTimeDate)
          : startEndFromTravelTimeOnly(sheet.createdAt, sheet.startTime, timeFormat)
        )
      const eventEnd = end ? new Date(end) : new Date(eventStart);

      const height = getSheetHeightInPixels(sheet, title, selectedDate);
      const heightInMinutes = (1440 * height) / 1080;
      const heightStart = new Date(Math.max(new Date(eventStart).getTime(), startOfDay(new Date(selectedDate)).getTime()))
      const heightEnd = add(heightStart, { minutes: heightInMinutes });

      const dayEnd = endOfDay(selectedDate)

      let renderEnd = new Date(eventEnd);
      if (isAfter(new Date(eventEnd), heightEnd)) {
        renderEnd = heightEnd;
      }
      if (isAfter(renderEnd, dayEnd)) {
        renderEnd = dayEnd;
      }

      return {
        id: sheet._id,
        title: title,
        start: eventStart,
        end: renderEnd,
        extendedProps: {
          ...entry,
          height,
          heightInMinutes,
          heightEnd,
          heightStart,
          locationColor,
        },
      };
    });
  }, [visibleSheets, showAllLocations, locations, selectedDate]).sort((a, b) => {
    const startDiff = new Date(a.start).getTime() - new Date(b.start).getTime();
    const endDiff = new Date(b.start).getTime() - new Date(a.start).getTime();
    return startDiff || endDiff;
  });

  return events;
};

export default useV6TimeEvents;

